import { useCallback, useEffect, useRef, useState } from 'react';
import { e2eRunnerService } from '../../services/e2e-runner/e2e-runner.service';
import type { E2eRunTier, E2eRunnerStatusView } from '../../services/e2e-runner/e2e-runner.service';

export const E2E_POLL_INTERVAL_MS = 10_000;
export const E2E_POLL_BACKOFF_MS = 60_000;
const MAX_FAILURES_BEFORE_BACKOFF = 3;

export type E2eRunnerController = {
  status: E2eRunnerStatusView | null;
  isBusy: boolean;
  error: string | null;
  statusError: boolean;
  pendingTier: E2eRunTier | null;
  requestLaunch: (tier: E2eRunTier) => void;
  cancelLaunch: () => void;
  confirmLaunch: () => void;
  stopRun: () => void;
};

// Architectural summary: polls status while visible; start/stop sit behind a ref lock (Rule 44).
export const useE2eRunnerController = (isActive: boolean): E2eRunnerController => {
  const [status, setStatus] = useState<E2eRunnerStatusView | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingTier, setPendingTier] = useState<E2eRunTier | null>(null);
  const [statusError, setStatusError] = useState(false);
  const inFlightRef = useRef(false);
  const pollingRef = useRef(false);
  const failureCountRef = useRef(0);

  const refresh = useCallback(async () => {
    if (pollingRef.current) {
      return;
    }
    pollingRef.current = true;
    try {
      const next = await e2eRunnerService.getStatus();
      failureCountRef.current = 0;
      setStatus(next);
      setError(null);
      setStatusError(false);
    } catch {
      failureCountRef.current += 1;
      setStatusError(failureCountRef.current >= MAX_FAILURES_BEFORE_BACKOFF);
    } finally {
      pollingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    // Clearing first keeps a visibilitychange from starting a second parallel poll chain.
    const tick = async () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (stopped || document.visibilityState !== 'visible') {
        return;
      }
      await refresh();
      if (stopped) {
        return;
      }
      const delay = failureCountRef.current >= MAX_FAILURES_BEFORE_BACKOFF ? E2E_POLL_BACKOFF_MS : E2E_POLL_INTERVAL_MS;
      timer = setTimeout(() => {
        void tick();
      }, delay);
    };

    void tick();
    document.addEventListener('visibilitychange', tick);

    return () => {
      stopped = true;
      if (timer) {
        clearTimeout(timer);
      }
      document.removeEventListener('visibilitychange', tick);
    };
  }, [isActive, refresh]);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsBusy(true);
      try {
        await action();
        setError(null);
        await refresh();
      } catch (actionError: unknown) {
        setError(actionError instanceof Error ? actionError.message : 'הפעולה נכשלה');
      } finally {
        inFlightRef.current = false;
        setIsBusy(false);
      }
    },
    [refresh],
  );

  const requestLaunch = useCallback((tier: E2eRunTier) => {
    setPendingTier(tier);
  }, []);

  const cancelLaunch = useCallback(() => {
    setPendingTier(null);
  }, []);

  const confirmLaunch = useCallback(() => {
    if (!pendingTier) {
      return;
    }
    const tier = pendingTier;
    setPendingTier(null);
    void run(() => e2eRunnerService.start(tier));
  }, [pendingTier, run]);

  const stopRun = useCallback(() => {
    void run(() => e2eRunnerService.stop());
  }, [run]);

  return { status, isBusy, error, statusError, pendingTier, requestLaunch, cancelLaunch, confirmLaunch, stopRun };
};
